import {useCallback, useRef, useState} from 'react';
import {useIsMount} from '../useIsMount';

export interface UseFunctionWrapResult<TArgs extends unknown[], TResult> {
  fn: (...args: TArgs) => Promise<TResult>;
  loading: boolean;
  error: Error | undefined;
}

async function runCreate<TArgs extends unknown[], TResult>(
  createFn: (...args: TArgs) => Promise<TResult>,
  args: TArgs,
  isMounted: () => boolean,
  setLoading: (loading: boolean) => void,
  setError: (error: Error | undefined) => void,
): Promise<TResult> {
  setLoading(true);
  setError(undefined);
  try {
    const result = await createFn(...args);
    if (isMounted()) {
      setLoading(false);
      setError(undefined);
    }
    return result;
  } catch (err) {
    if (isMounted()) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
    throw err;
  }
}

/**
 * Wraps an async function and manages loading/error state.
 * The returned function forwards all arguments to the provided function and updates loading/error on success or failure.
 */
export function useFunctionWrap<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): UseFunctionWrapResult<TArgs, TResult> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const isMountedFn = useIsMount();
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const wrappedFn = useCallback(
    (...args: TArgs) => runCreate((...a: TArgs) => fnRef.current(...a), args, isMountedFn, setLoading, setError),
    [isMountedFn],
  );

  return {
    fn: wrappedFn,
    loading,
    error,
  };
}
